/**
 * ChaseTheAce Storybook — extracted GridEngine hero section.
 * Mirrors the inline `renderHeroSection` in `pages/layouts/ChaseTheAceStyleStorybook.jsx`.
 */
import React from 'react'
import StorybookHero from '../../../components/storybook/StorybookHero'
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import { useContent } from '../../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'
import { useCountdown } from '../../../hooks/useCountdown'
import { useCharityTheme } from '../../../contexts/CharityThemeContext'
import { useSiteConfig } from '../../../contexts/SiteConfigContext'
import { charityConfig } from '../../../config/charity'
import { useLanguage } from '../../../contexts/LanguageContext'
import { useTranslations } from '../../../contexts/TranslationContext'
import { resolveCtaTargetCard, formatTargetCardName, formatCtaTargetCardText } from '../../../utils/ctaHelpers'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'

export function ChaseStorybookHeroSection({
  mainDraw,
  eliminatedCards = [],
  onLoginClick,
  editMode,
  handleTranslationUpdate,
  sections = [],
  isAdmin,
  updateSection,
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const { branding } = useCharityTheme()
  const { language } = useLanguage()
  const { siteDetails } = useSiteConfig() || {}
  const { timeLeft } = useCountdown(mainDraw, { enableAnimation: true })

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const weeklyPrize = Math.floor(mainDraw?.weekly_prize ?? mainDraw?.secondary_prize ?? 0)
  const cardsRemaining = mainDraw?.cards_remaining ?? (52 - (eliminatedCards?.length ?? 0))
  const ctaTarget = resolveCtaTargetCard(sections, siteDetails)
  const ctaTargetName = formatTargetCardName(ctaTarget.rank, ctaTarget.suit, language)
  const targetCopy = (key, fallback) => formatCtaTargetCardText(c(key, fallback), ctaTarget, fallback, language)

  const heroSectionConfig = sections.find(s => s.section_type === 'hero_section')?.config ?? {}
  const showFloatingCards = heroSectionConfig.showFloatingCards ?? true
  const logoBackgroundColor = heroSectionConfig.logoBackgroundColor ?? '#ffffff'

  const heroSect = sections.find(s => s.section_type === 'hero_section')
  const handleHeroConfigChange = (patch) => {
    if (!heroSect || !updateSection) return
    updateSection(heroSect.id, { config: { ...heroSect.config, ...patch } })
  }

  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl
  const brandName = siteDetails?.siteName || charityConfig.name
  const defaultPlatformLogo = 'https://cms.tlffs.ca/uploads/header_Logo_4f095fb08b.svg'
  const isDefault = branding?.logoUrl === defaultPlatformLogo
  const charityLogoUrl = isDefault ? brandLogo : (branding?.logoUrl || brandLogo)

  return (
    <div key="hero_section" className="relative">
      {isAdmin && (
        <div className="absolute top-4 right-4 z-50 rounded-xl bg-black/60 backdrop-blur-md border border-white/20 p-3 flex flex-col gap-2.5 min-w-[160px]">
          <p className="text-[10px] font-semibold text-white/60 uppercase tracking-wider">{tr('admin.heroSettings.title', 'Hero Settings')}</p>
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-white">{tr('admin.heroSettings.floatingCards', 'Floating cards')}</span>
            <button
              onClick={() => handleHeroConfigChange({ showFloatingCards: !showFloatingCards })}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none ${showFloatingCards ? 'bg-indigo-500' : 'bg-gray-500/70'}`}
              role="switch"
              aria-checked={showFloatingCards}
              aria-label={tr('admin.heroSettings.floatingCards', 'Floating cards')}
            >
              <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${showFloatingCards ? 'translate-x-4' : 'translate-x-0'}`} />
            </button>
          </div>
          <div>
            <span className="text-xs text-white block mb-1.5">{tr('admin.heroSettings.logoBackground', 'Logo background')}</span>
            <div className="flex gap-1.5 flex-wrap">
              {[
                { label: tr('admin.heroSettings.white', 'White'), value: '#ffffff' },
                { label: tr('admin.heroSettings.clear', 'Clear'), value: 'transparent' },
                { label: tr('admin.heroSettings.black', 'Black'), value: '#000000' },
                { label: tr('admin.heroSettings.dark', 'Dark'), value: '#1a1a1a' },
              ].map(preset => (
                <button
                  key={preset.value}
                  title={preset.label}
                  aria-label={`${tr('admin.heroSettings.logoBackground', 'Logo background')} ${preset.label}`}
                  onClick={() => handleHeroConfigChange({ logoBackgroundColor: preset.value })}
                  className={`w-5 h-5 rounded-full border-2 transition-all ${logoBackgroundColor === preset.value ? 'border-indigo-400 scale-110' : 'border-white/40'}`}
                  style={{
                    backgroundColor: preset.value === 'transparent' ? undefined : preset.value,
                    backgroundImage: preset.value === 'transparent' ? 'repeating-conic-gradient(#ccc 0% 25%, white 0% 50%)' : undefined,
                    backgroundSize: '4px 4px',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
      <StorybookHero
        weekNumber={mainDraw?.week_number ?? mainDraw?.current_week_number}
        liveSlot={<InlineTranslationEdit translationKey="chaseTheAce.liveNow" value={c('chaseTheAce.liveNow')} fallback="LIVE NOW" onUpdate={handleTranslationUpdate} editMode={editMode} />}
        descriptionSlot={<InlineTranslationEdit translationKey="chaseTheAce.subtitle" value={targetCopy('chaseTheAce.subtitle', `Draw a card. Find the ${ctaTargetName} and win the progressive jackpot!`)} fallback={`Draw a card. Find the ${ctaTargetName} and win the progressive jackpot!`} onUpdate={handleTranslationUpdate} editMode={editMode} />}
        buyTicketsSlot={<InlineTranslationEdit translationKey="chaseTheAce.buyTickets" value={c('chaseTheAce.buyTickets')} fallback="Get Your Tickets" onUpdate={handleTranslationUpdate} editMode={editMode} />}
        findAceSlot={<InlineTranslationEdit translationKey="chaseTheAce.findAceTagline" value={targetCopy('chaseTheAce.findAceTagline', `Find the ${ctaTargetName} to win!`)} fallback={`Find the ${ctaTargetName} to win!`} onUpdate={handleTranslationUpdate} editMode={editMode} />}
        previousDrawsSlot={<InlineTranslationEdit translationKey="chaseTheAce.previousDraws" value={c('chaseTheAce.previousDraws')} fallback="Previous Draws" onUpdate={handleTranslationUpdate} editMode={editMode} />}
        drawInSlot={<InlineTranslationEdit translationKey="chaseTheAce.drawIn" value={c('chaseTheAce.drawIn')} fallback="Next Draw In" onUpdate={handleTranslationUpdate} editMode={editMode} />}
        progressiveJackpotSlot={<InlineTranslationEdit translationKey="chaseTheAce.progressiveJackpot" value={c('chaseTheAce.progressiveJackpot')} fallback="Progressive Jackpot" onUpdate={handleTranslationUpdate} editMode={editMode} />}
        weeklyPrizeSlot={<InlineTranslationEdit translationKey="chaseTheAce.weeklyPrize" value={c('chaseTheAce.weeklyPrize')} fallback="Weekly Prize" onUpdate={handleTranslationUpdate} editMode={editMode} />}
        getTicketsSlot={<InlineTranslationEdit translationKey="chaseTheAce.getTickets" value={c('chaseTheAce.getTickets')} fallback="Get Tickets" onUpdate={handleTranslationUpdate} editMode={editMode} />}
        partnersHeaderSlot={<InlineTranslationEdit translationKey="chaseTheAce.partnersHeader" value={c('chaseTheAce.partnersHeader')} fallback="Partners" onUpdate={handleTranslationUpdate} editMode={editMode} />}
        partnerSelectSlot={<HeroPartnerGlassSelect className="mx-auto w-full max-w-md" />}
        charityLogoUrl={charityLogoUrl}
        charityName={branding?.charityName || brandName || 'Charity'}
        siteLogoUrl={siteDetails?.siteLogo}
        siteName={siteDetails?.operatingName || siteDetails?.name || branding?.charityName}
        jackpotAmount={jackpotAmount}
        weeklyPrize={weeklyPrize}
        cardsRemaining={cardsRemaining}
        eliminatedCards={eliminatedCards}
        timeLeft={timeLeft}
        targetCard={ctaTarget}
        showFloatingCards={showFloatingCards}
        logoBackgroundColor={logoBackgroundColor}
        onBuyTickets={() => purchaseNavigate('/chase-the-ace-purchase')}
      />
    </div>
  )
}
